/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../../layouts/MainLayout";
import "../../../assets/styles/custom-styles.css";
import { addressFields } from "../../../constants/forms/addressFields";
import Form from "../../../components/forms/Form";
import useLoader from "../../../hooks/useLoader";
import useNotification from "../../../hooks/useNotification";
import useForm from "../../../hooks/useForm";
import { maskCep, removeMask } from "../../../utils/maskUtils";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useSelectOptions from "../../../hooks/useSelectOptions";
import useRemoteSelectOptions from "../../../hooks/useRemoteSelectOptions";

const CreateOrganizationAddressPage = () => {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  const { post: baseServicePost, formErrors } = useBaseService(navigate);
  const { formData, setFormData, handleChange } = useForm(
    setDefaultFieldValues(addressFields)
  );

  const stateSelect = useRemoteSelectOptions({
    queryKey: ["stateOptions"],
    fetcher: async () =>
      await fetch(`https://brasilapi.com.br/api/ibge/uf/v1`).then((res) =>
        res.json()
      ),
    fieldId: "address.state",
    formData,
    isMulti: false,
    mapToOption: (type) => ({ value: type.nome, label: type.nome }),
  });

  const rawCountryOptions = useMemo(
    () => [{ value: "Brasil", label: "Brasil" }],
    []
  );

  const countrySelect = useSelectOptions({
    fieldId: "address.country",
    formData,
    rawOptions: rawCountryOptions,
    isMulti: false,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
  });

  const selects = {
    "address.state": stateSelect,
    "address.country": countrySelect,
  };

  // --- Funções para Selects (getOptions e getSelectedValue) ---
  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.options || [];
    },
    [selects]
  );

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [selects]
  );

  const handleFieldChange = useCallback(
    async (fieldId, value) => {
      if (fieldId === "address.zip") {
        handleCepChange(value);
      } else {
        handleChange(fieldId, value);
      }
    },
    [handleChange]
  );

  const handleCepChange = useCallback(
    async (cep) => {
      const value = maskCep(cep);
      const zip = removeMask(value);

      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          zip: value,
        },
      }));

      if (zip.length === 8) {
        showLoader();
        try {
          const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
          if (!response.ok) throw new Error("Erro ao buscar o CEP");

          const data = await response.json();
          if (data.erro) throw new Error("CEP não encontrado");

          setFormData((prev) => ({
            ...prev,
            address: {
              ...prev.address,
              street: data.logradouro || "",
              district: data.bairro || "",
              city: data.localidade || "",
              state: data.estado || "",
              country: "Brasil" || "",
            },
          }));

          showNotification("success", "Endereço preenchido automaticamente!");
        } catch (error) {
          showNotification("error", error.message);
        } finally {
          hideLoader();
        }
      }
    },
    [setFormData, showNotification, showLoader, hideLoader]
  );

  const create = useMutation({
    mutationFn: async (data) => {
      const response = await baseServicePost(
        entities.organizations.addresses.create(organizationId),
        data
      );
      return response.result;
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        navigate(`/aluguel/organizacoes/detalhes/${organizationId}`);
        queryClient.invalidateQueries({
          queryKey: ["addressOrganization", organizationId],
        });
      }
    },
    onError: (error) => {
      console.error("onError ", error);
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      showLoader();

      const sanitizedData = {
        address: {
          ...formData.address,
          addressable_type: "organization",
          number: formData.address.number ? String(formData.address.number) : null,
          zip: formData.address.zip ? removeMask(formData.address.zip) : "",
        },
      };

      return await create.mutateAsync(sanitizedData);
    } catch (error) {
      console.error("Erro ao cadastrar endereço: ", {
        status: error.status,
        message: error.message,
      });
    }
  }, [showLoader, formData.address, create]);

  const handleBack = useCallback(() => {
    navigate(`/aluguel/organizacoes/detalhes/${organizationId}/`);
  }, [navigate, organizationId]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de Endereço da Organização"
        showBackButton={true}
        backUrl={`/aluguel/organizacoes/detalhes/${organizationId}/`}
      />
      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Cadastrar"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}
        >
          {() => {
            const renderedSections = new Set();

            return addressFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleFieldChange}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateOrganizationAddressPage;
